import { useEffect, useState } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Grid } from '@deliveryhero/pd-cookbook/components/Grid';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';
import { CardActionArea, CardMedia } from '@mui/material';

import { Card } from 'components/Card';
import { GiftCardPurchaseForm } from 'components/Forms/GiftCardPurchase';
import { FormLabelText } from 'components/Forms/GiftCardPurchase/constants';
import { GiftCardPurchaseFormValues } from 'components/Forms/GiftCardPurchase/types';
import { FtueTooltip } from 'components/FtueTooltip/FtueTooltip';
import { GiftCardProducts } from 'components/GiftCard/GiftCardProducts';
import { OrderSummary } from 'components/GiftCard/OrderSummary';
import { GiftCardPage } from 'components/GiftCard/types';
import { LoadingAnimation } from 'components/LoadingAnimation';
import { GiftCardProduct, GiftCardThemeType } from 'services/api';

import { LabelText } from './constants';

import './styles.scss';

export const AddGiftCard = (props: GiftCardPage) => {
  const {
    buttons,
    config,
    data,
    detailFormRef,
    handleOrderSummaryChange,
    handleSelectCard,
    orderSummary,
    selectedCard,
    enableSubmit,
    onActionClick,
    csvRecipients,
    handleCsvUploadCancel,
    resetTour,
    runTour,
    tourSteps,
    isLoading,
    placeOrderLoading,
    discount,
    ...others
  } = props;

  const [show, setShow] = useState(false);
  const product: GiftCardProduct = data[0]; // currently only supports 1 product

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, status, type } = data;

    if (
      ((action == ACTIONS.CLOSE && status == STATUS.FINISHED) ||
        (action == ACTIONS.SKIP && status == STATUS.SKIPPED)) &&
      type == EVENTS.TOUR_END
    ) {
      resetTour();
    }
  };

  /* this delay is added to resolve the issue of react-joyride 
  starting the tour before the full dialog page slide-up animation
  ends which results in incorrect placement of the spotlight due
  to snapshotting */
  useEffect(() => {
    if (!runTour) setShow(true);
    const timeout = setTimeout(() => {
      setShow(true);
    }, 500);

    return () => clearTimeout(timeout);
  }, [show]);

  if (isLoading) {
    return <LoadingAnimation />;
  }

  const initialFormValues: GiftCardPurchaseFormValues = {
    distribute_myself: false,
    message: '',
    total: 0,
    subtotal: 0,
    quantity: '',
    recipients: [{ name: '', email: '' }],
    sku: product.sku,
    theme: '',
    use_csv: false,
    value: ''
  };

  return (
    <>
      {show && (
        <Joyride
          run={runTour}
          floaterProps={{ hideArrow: true, placement: 'right' }}
          scrollDuration={500}
          callback={handleJoyrideCallback}
          spotlightPadding={0}
          tooltipComponent={(props) => (
            <FtueTooltip
              stepOffset={props.index >= 1 ? '96px' : '38px'}
              {...props}
            />
          )}
          steps={tourSteps}
          continuous={true}
          showProgress={true}
          showSkipButton
          disableOverlayClose
          disableCloseOnEsc
          disableScrollParentFix={true}
          scrollOffset={85}
          styles={{
            options: {
              zIndex: 1500,
              overlayColor: 'rgba(51, 51, 51, 0.56)'
            },
            spotlight: { borderRadius: '8px' }
          }}
        />
      )}
      <Grid
        gridTemplateColumns="1fr 4fr 1fr"
        style={{
          ...(!runTour &&
            selectedCard && {
              animation: 'resize 1000ms',
              animationFillMode: 'forwards'
            })
        }}
      >
        <Grid />
        <Grid rowGap="16px" style={{ marginRight: '24px' }}>
          <Card className="gift-card-spotlight-products">
            <GiftCardProducts
              data={data}
              selectedCard={
                runTour
                  ? { sku: product.sku, image: product.themes[0].image }
                  : selectedCard
              }
              handleClick={handleSelectCard}
            ></GiftCardProducts>
          </Card>
          <Card
            style={{
              display: runTour ? 'grid' : 'none',
              opacity: runTour ? 1 : 0,
              ...(!runTour &&
                selectedCard && {
                  display: 'grid',
                  animation: 'fadeIn 700ms ease-in',
                  animationFillMode: 'forwards'
                })
            }}
          >
            <GiftCardPurchaseForm
              initialValues={initialFormValues}
              buttons={buttons}
              config={config}
              csvRecipients={csvRecipients}
              data={data}
              enableSubmit={enableSubmit}
              detailFormRef={detailFormRef}
              selectedCard={selectedCard as GiftCardThemeType}
              handleOrderSummaryChange={handleOrderSummaryChange}
              onActionClick={onActionClick}
              handleCsvUploadCancel={handleCsvUploadCancel}
              {...others}
            />
          </Card>
        </Grid>
        {!runTour && (
          <Grid
            style={{
              opacity: 0,
              display: 'none',
              rowGap: '16px',
              gridTemplateRows: 'auto auto auto auto 1fr',
              alignSelf: 'start',
              position: 'sticky',
              top: '104px',
              ...(selectedCard && {
                display: 'grid',
                animation: 'fadeIn 700ms ease-in 500ms',
                animationFillMode: 'forwards'
              })
            }}
          >
            <Card style={{ overflow: 'hidden' }}>
              <CardActionArea>
                {selectedCard && (
                  <CardMedia
                    sx={{
                      borderRadius: '8px',
                      outline: 'solid 1px #DCDCDC',
                      outlineOffset: '-1px'
                    }}
                    component="img"
                    image={selectedCard.image}
                  />
                )}
              </CardActionArea>
              <Typography
                type="title-sm"
                color={
                  orderSummary.giftCardMessage != FormLabelText.MESSAGE_DEFAULT
                    ? 'neutral-primary'
                    : 'neutral-secondary'
                }
                style={{ overflowWrap: 'break-word' }}
              >
                {orderSummary.giftCardMessage}
              </Typography>
            </Card>
            {Boolean(orderSummary.quantity && orderSummary.giftCardValue) && (
              <Card>
                <OrderSummary
                  config={config}
                  orderSummaryDetails={orderSummary}
                />
              </Card>
            )}
            <Button
              layout="full-width-primary"
              disabled={!enableSubmit}
              status={
                placeOrderLoading || discount.isLoading
                  ? 'is-loading'
                  : 'is-idle'
              }
              onClick={() => detailFormRef.current?.handleSubmit()}
            >
              {LabelText.ADD_BUTTON}
            </Button>
            <Typography type="label-sm" color="neutral-primary">
              {LabelText.PLACE_ORDER_INFORMATION}
            </Typography>
          </Grid>
        )}
      </Grid>
    </>
  );
};
